<script lang="ts">
  /**
   * The estate map — every portal, its address, its GCP project and who may see it.
   *
   * This is the "ten projects" table from 02-frontdoor.md §4, moved here rather than
   * removed. The spec is explicit about it: *"This is Christo's own map of his estate
   * and he asked for it explicitly — keep it."* Keeping it and giving it a page of
   * its own is the point of the redesign: it was never the wrong content, only the
   * wrong thing for a landing page to open with.
   *
   * Grouped by GCP project rather than listed flat. Four modules now share
   * `steyn-fabric`, so the project id stopped identifying a portal and started
   * identifying a *blast radius* — which is the fact worth seeing on a map. A flat
   * list buries it; the grouping states it.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { estate, host, ICON, type Portal } from "../lib/estate.svelte";

  type Group = { projectId: string; portals: Portal[] };

  const grouped = $derived.by(() => {
    const by = new Map<string, Portal[]>();
    for (const p of [...estate.portals].sort((a, b) => a.order - b.order)) {
      const key = p.projectId || "unassigned";
      const list = by.get(key);
      if (list) list.push(p);
      else by.set(key, [p]);
    }
    // Biggest blast radius first: the project carrying four portals is the one whose
    // configuration matters most, and it should not be third on the page.
    return [...by.entries()]
      .map(([projectId, portals]): Group => ({ projectId, portals }))
      .sort((a, b) => b.portals.length - a.portals.length || a.projectId.localeCompare(b.projectId));
  });

  const ROLE_NOTE: Record<string, string> = {
    owner: "Christo only",
    partner: "Christo and Mandri",
    display: "also the lounge screen",
    guest: "also a house-sitter",
  };
</script>

<div class="wrap">
  <header class="head">
    <h1>The estate</h1>
    <p class="sub">
      Every portal, where it lives and who may open it. Grouped by Google Cloud project,
      because several portals now share one — which makes the project the boundary that
      matters when something is changed.
    </p>
  </header>

  {#if estate.error}
    <div class="attn attn--warn">
      <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
      <span class="attn__text">{estate.error}</span>
      <button class="btn-primary" onclick={() => estate.load()}>Retry</button>
    </div>
  {/if}

  {#each grouped as g (g.projectId)}
    <section class="card proj">
      <div class="proj__head">
        <span class="proj__id num">{g.projectId}</span>
        <span class="proj__n">
          {g.portals.length}
          {g.portals.length === 1 ? "portal" : "portals"}
          {#if g.portals.length > 1}
            <!-- Said out loud rather than left to be inferred from the grouping. -->
            <span class="proj__warn"><Icon name="alert" size={11} /> shared blast radius</span>
          {/if}
        </span>
      </div>

      <div class="scroll">
        <table>
          <thead>
            <tr><th>Portal</th><th>Address</th><th>Who may open it</th><th>State</th></tr>
          </thead>
          <tbody>
            {#each g.portals as p (p.id)}
              <tr>
                <td>
                  <span class="nm"><Icon name={ICON[p.id] ?? "door"} size={14} /> {p.name}</span>
                  <span class="blurb">{p.blurb}</span>
                </td>
                <td data-label="Address">
                  {#if p.status === "live"}
                    <a class="num addr" href={p.url}>{host(p.url)}</a>
                  {:else}
                    <!-- Not a link: the address is reserved, not reachable, and a
                         dead link on a map is worse than a plain string. -->
                    <span class="num addr addr--off">{host(p.url)}</span>
                  {/if}
                </td>
                <td class="sub" data-label="Who may open it">
                  {p.minRole}
                  {#if ROLE_NOTE[p.minRole]}<span class="rolenote">· {ROLE_NOTE[p.minRole]}</span>{/if}
                </td>
                <td data-label="State">
                  {#if p.status !== "live"}
                    <span class="pill quiet"><Icon name="pause" size={11} /> {p.status}</span>
                  {:else if p.health === null}
                    <span class="pill quiet"><Icon name="pause" size={11} /> not checked</span>
                  {:else if p.health.ok && p.health.slow}
                    <span class="pill warn"><Icon name="clock" size={11} /> live · slow</span>
                  {:else if p.health.ok}
                    <span class="pill ok"><Icon name="check" size={11} /> live</span>
                  {:else}
                    <span class="pill warn"><Icon name="alert" size={11} /> not responding</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </section>
  {/each}

  {#if estate.loading && grouped.length === 0}
    <p class="sub">Loading the estate…</p>
  {/if}
</div>

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-bottom: 40px;
  }
  .head {
    padding: 22px 0 0;
  }
  h1 {
    margin: 0 0 6px;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.7px;
    color: var(--tx);
  }
  .head .sub {
    max-width: 66ch;
    font-size: 0.7812rem;
    line-height: 1.5;
  }

  .proj {
    padding: 4px 4px 2px;
  }
  .proj__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px 10px;
  }
  .proj__id {
    font-size: 0.8125rem;
    font-weight: 800;
    color: var(--tx);
  }
  .proj__n {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }
  .proj__warn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--warn);
    font-weight: 800;
  }

  .scroll {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.7812rem;
  }
  th {
    padding: 8px 14px;
    text-align: left;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--mut);
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
  td {
    padding: 10px 14px;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }
  tr:last-child td {
    border-bottom: none;
  }
  .nm {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-weight: 700;
    color: var(--tx);
  }
  .blurb {
    display: block;
    margin-top: 2px;
    max-width: 34ch;
    font-size: 0.72rem;
    line-height: 1.4;
    color: var(--mut);
  }
  .addr {
    font-size: 0.72rem;
    color: var(--tx2);
  }
  .addr:hover {
    color: var(--acc);
  }
  .addr--off {
    color: var(--mut);
    text-decoration: line-through;
    text-decoration-thickness: 1px;
  }
  .rolenote {
    color: var(--mut);
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: var(--r-pill);
    font-size: 0.72rem;
    font-weight: 800;
    white-space: nowrap;
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .ok {
    color: var(--ok);
  }
  .warn {
    color: var(--warn);
  }
  .quiet {
    color: var(--mut);
  }

  /* Under 700px a six-column table cannot be read by scrolling sideways, so each
     row becomes a stacked block with its column name as a label. `content` from a
     data-attribute rather than duplicated markup, so there is one source for the
     header text and no chance of the two disagreeing. */
  @media (max-width: 700px) {
    thead {
      display: none;
    }
    tr {
      display: block;
      padding: var(--sp-3, 12px) 0;
      border-bottom: 1px solid var(--line);
    }
    td {
      display: flex;
      justify-content: space-between;
      gap: var(--sp-3, 12px);
      padding: 3px 0;
      border: none;
      text-align: right;
    }
    td::before {
      content: attr(data-label);
      font-size: var(--fs-micro);
      font-weight: 800;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: var(--mut);
      text-align: left;
    }
    td:first-child {
      display: block;
      text-align: left;
      margin-bottom: var(--sp-1, 4px);
    }
    td:first-child::before {
      content: none;
    }
  }
</style>
